import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { SavedAPIAddress } from '~/types/APIAddress';
import ParcelTemplate from '~/types/ParcelTemplate';
import { RateAtCheckoutErrorArray, ShippingOptionPayload } from '~/types/RatesAtCheckout';
import { getFailed, getNotRequested, getRequesting, getSuccess } from '~/utils/fetchable';

export type RatesAtCheckoutState = {
  readonly addShippingOptionModalIsOpen: boolean;
  readonly shippingOptions: Requestable<ShippingOptionPayload[], string>;
  readonly createShippingOptionStatus: Requestable<ShippingOptionPayload, RateAtCheckoutErrorArray>;
  readonly updateShippingOptionStatus: Requestable<ShippingOptionPayload, RateAtCheckoutErrorArray>;
  readonly deleteShippngOptionStatus: Requestable<string, string>;
  readonly liveRatePackage: Requestable<{ result: ParcelTemplate }, string>;
  readonly updateLiveRatePackageStatus: Requestable<ParcelTemplate, string>;
  readonly liveRateSenderAddress: Requestable<SavedAPIAddress, string>;
  readonly updateLiveRateSenderAddressStatus: Requestable<SavedAPIAddress, string>;
};

const initialState: RatesAtCheckoutState = {
  addShippingOptionModalIsOpen: false,
  shippingOptions: getNotRequested(),
  createShippingOptionStatus: getNotRequested(),
  updateShippingOptionStatus: getNotRequested(),
  deleteShippngOptionStatus: getNotRequested(),
  liveRatePackage: getNotRequested(),
  updateLiveRatePackageStatus: getNotRequested(),
  liveRateSenderAddress: getNotRequested(),
  updateLiveRateSenderAddressStatus: getNotRequested(),
};

function ratesAtCheckoutReducer(
  state: RatesAtCheckoutState = initialState,
  action: ActionTypes,
): RatesAtCheckoutState {
  switch (action.type) {
    case ActionKeys.RATES_AT_CHECKOUT_OPEN_ADD_RULE_MODAL:
      return {
        ...state,
        addShippingOptionModalIsOpen: true,
      };
    case ActionKeys.RATES_AT_CHECKOUT_CLOSE_ADD_RULE_MODAL:
      // reset Errors to initial value on Close
      const updateStateValues: Record<string, unknown> = {};
      const resetErrors: (keyof RatesAtCheckoutState)[] = [
        'createShippingOptionStatus',
        'updateShippingOptionStatus',
        'deleteShippngOptionStatus',
      ];
      for (const item of resetErrors) {
        const entry = state[item];
        if (typeof entry !== 'boolean' && entry.kind === 'failed') {
          updateStateValues[item] = initialState[item];
        }
      }
      return { ...state, addShippingOptionModalIsOpen: false, ...updateStateValues };

    case ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION:
      return {
        ...state,
        createShippingOptionStatus: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION_SUCCESS:
      return {
        ...state,
        createShippingOptionStatus: getSuccess(action.payload.data),
      };
    case ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION_ERROR:
      return {
        ...state,
        createShippingOptionStatus: getFailed(action.payload.errors),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS:
      return {
        ...state,
        shippingOptions: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS_SUCCESS:
      return {
        ...state,
        shippingOptions: getSuccess(action.payload.data),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS_ERROR:
      return {
        ...state,
        shippingOptions: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION:
      return {
        ...state,
        updateShippingOptionStatus: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION_SUCCESS:
      return {
        ...state,
        updateShippingOptionStatus: getSuccess(action.payload.data),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION_ERROR:
      return {
        ...state,
        updateShippingOptionStatus: getFailed(action.payload.errors),
      };
    case ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION:
      return {
        ...state,
        deleteShippngOptionStatus: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION_SUCCESS:
      return {
        ...state,
        deleteShippngOptionStatus: getSuccess(action.payload.data),
      };
    case ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION_ERROR:
      return {
        ...state,
        deleteShippngOptionStatus: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE:
      return {
        ...state,
        liveRatePackage: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE_SUCCESS:
      return {
        ...state,
        liveRatePackage: getSuccess(action.payload.data),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE_ERROR:
      return {
        ...state,
        liveRatePackage: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE:
      return {
        ...state,
        updateLiveRatePackageStatus: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE_SUCCESS:
      return {
        ...state,
        updateLiveRatePackageStatus: getSuccess(action.payload.data),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE_ERROR:
      return {
        ...state,
        updateLiveRatePackageStatus: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS:
      return {
        ...state,
        liveRateSenderAddress: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS_SUCCESS:
      return {
        ...state,
        liveRateSenderAddress: getSuccess(action.payload.senderAddress),
      };
    case ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS_ERROR:
      return {
        ...state,
        liveRateSenderAddress: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS:
      return {
        ...state,
        updateLiveRateSenderAddressStatus: getRequesting(),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS_SUCCESS:
      return {
        ...state,
        updateLiveRateSenderAddressStatus: getSuccess(action.payload.senderAddress),
      };
    case ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS_ERROR:
      return {
        ...state,
        updateLiveRateSenderAddressStatus: getFailed(action.payload.errorMessage),
      };
    default:
      return state;
  }
}

export default ratesAtCheckoutReducer;
